import { Button, Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import { Assignment } from '@material-ui/icons';
import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  useQuizAssessmentTestDataProvider
} from '../../../../providers/data-provider/quiz-assessment-test.data.provider';
import { useAnswerKey } from './use-answer-key';
import { useTestChangeLog } from './use-test-change-log';
import { AnswerKeyLoading } from './answer-key-loading';
import { AnswerKeyTable } from './answer-key-table';
import { AnswerKeyDialogActions } from './answer-key-dialog-actions';
import { areAllQuestionsAnswered, getSelectedAnswers, toTestResponse } from './utils';

export function AnswerKeyAction({ testId, studentName, onSubmitted, status }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [givenAnswers, setGivenAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const { answerKey, loading, reload } = useAnswerKey(testId, open);
  const { changeLog, reload: reloadChangeLog } = useTestChangeLog(testId, open);
  const { editTestResponse } = useQuizAssessmentTestDataProvider();
  const notify = useNotify();

  useEffect(() => {
    setEditing(false);
  }, [open, testId]);

  const handleStartEditing = useCallback(() => {
    setGivenAnswers(getSelectedAnswers(answerKey));
    setEditing(true);
  }, [answerKey]);

  const handleCancelEditing = useCallback(() => {
    setGivenAnswers({});
    setEditing(false);
  }, []);

  const handleSelect = useCallback((questionId, optionId) => {
    setGivenAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  }, []);

  const handleSaveEdition = useCallback(() => {
    setSubmitting(true);
    editTestResponse(testId, toTestResponse(givenAnswers)).then(() => {
      notify('Respostas atualizadas com sucesso !', 'success');
      setEditing(false);
      setGivenAnswers({});
      reload();
      reloadChangeLog();
      onSubmitted?.();
    }).catch(() => {
      notify('Erro ao atualizar respostas !', 'error');
    }).finally(() => {
      setSubmitting(false);
    });
  }, [editTestResponse, testId, givenAnswers, notify, reload, reloadChangeLog, onSubmitted]);

  if (status !== 'finished') {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Assignment />}
        onClick={() => setOpen(true)}
      >
        Respostas
      </Button>
      <Dialog open={open} onClose={() => !submitting && setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Gabarito{studentName ? ` - ${studentName}` : ''}</DialogTitle>
        <DialogContent>
          {loading ? (
            <AnswerKeyLoading />
          ) : (
            <AnswerKeyTable
              answerKey={answerKey}
              editing={editing}
              givenAnswers={givenAnswers}
              onSelect={handleSelect}
              disabled={submitting}
            />
          )}
        </DialogContent>
        <AnswerKeyDialogActions
          editing={editing}
          submitting={submitting}
          canSave={areAllQuestionsAnswered(answerKey, givenAnswers)}
          canEdit={!loading && answerKey.length > 0}
          changeLog={changeLog}
          studentName={studentName}
          onClose={() => setOpen(false)}
          onStartEditing={handleStartEditing}
          onCancelEditing={handleCancelEditing}
          onSave={handleSaveEdition}
        />
      </Dialog>
    </>
  );
}
